import { Fragment, useMemo, useState } from "react"
import {
  EmailStats,
  ExitPopupConversionFunnel,
  LeadByPropertyAndResidency,
  LeadByStateAndCity,
  LeadConversionFunnel,
  LeadConversionProgress,
  LeadGenerationTrend,
  LeadList,
  RateTableUsageMetrics,
  SubscriptionCount,
  TopLeadBreakdown,
  UTMParameters,
  withLazyLoad
} from "../../common/ui"
import { useUserRoles } from "../../common/hooks"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import DaysFilter from "../../common/ui/days-filter"
import { DateRange } from "../../types"

const LazyLoadedEmailStats = withLazyLoad(EmailStats)
const LazyLoadedExitPopupConversionFunnel = withLazyLoad(
  ExitPopupConversionFunnel
)
const LazyLoadedLeadByPropertyAndResidency = withLazyLoad(
  LeadByPropertyAndResidency
)
const LazyLoadedLeadByStateAndCity = withLazyLoad(LeadByStateAndCity)
const LazyLoadedLeadConversionFunnel = withLazyLoad(LeadConversionFunnel)
const LazyLoadedLeadConversionProgress = withLazyLoad(LeadConversionProgress)
const LazyLoadedLeadGenerationTrend = withLazyLoad(LeadGenerationTrend)
const LazyLoadedLeadList = withLazyLoad(LeadList)
const LazyLoadedRateTableUsageMetrics = withLazyLoad(RateTableUsageMetrics)
const LazyLoadedSubscriptionCount = withLazyLoad(SubscriptionCount)
const LazyLoadedTopLeadBreakdown = withLazyLoad(TopLeadBreakdown)
const LazyLoadedUTMParameters = withLazyLoad(UTMParameters)

const Stats = () => {
  const { user, isBankAdmin, isLoanOfficer, isSuperAdmin } = useUserRoles()

  const [range, setRange] = useState<DateRange>(30)

  const orgId = useMemo(() => {
    return !isSuperAdmin && isBankAdmin ? user?.org?.id : undefined
  }, [isBankAdmin, isSuperAdmin, user?.org?.id])
  const loid = useMemo(() => {
    return !isSuperAdmin && !isBankAdmin && isLoanOfficer ? user?.id : undefined
  }, [isBankAdmin, isLoanOfficer, isSuperAdmin, user?.id])

  const handleUpdateRange = (newValue: DateRange) => {
    setRange(newValue)
  }

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.stats}>
      <Fragment>
        <div className="bg-white flex flex-row items-center justify-between px-6 py-4">
          <div className="text-2xl font-medium">Stats</div>
          <DaysFilter range={range} handleUpdateRange={handleUpdateRange} />
        </div>
        <div
          id="stats-container"
          className="grid gap-6 grid-cols-12 p-6 pb-20 bg-primary-100"
        >
          {range && (
            <Fragment>
              <div className="col-span-12 lg:col-span-6 flex min-h-[450px]">
                <LazyLoadedLeadGenerationTrend
                  key={`lead-generation-trend-${range}`}
                  title="Lead Generation Trend"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[450px]">
                <LazyLoadedTopLeadBreakdown
                  key={`top-lead-breakdown-${range}`}
                  title="Top Lead Breakdown"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex">
                <LazyLoadedUTMParameters
                  key={`utm-parameters-${range}`}
                  title="UTM Parameters"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[450px]">
                <LazyLoadedLeadByStateAndCity
                  key={`lead-by-state-and-city-${range}`}
                  title="Leads by City and State"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[450px]">
                <LazyLoadedLeadByPropertyAndResidency
                  key={`lead-by-property-and-residency-${range}`}
                  title="Lead Stats"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[450px]">
                <LazyLoadedLeadConversionFunnel
                  key={`lead-conversion-funnel-${range}`}
                  title="Lead Conversion Funnel"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[450px]">
                <LazyLoadedLeadConversionProgress
                  key={`lead-conversion-progress-${range}`}
                  title="Lead Conversion Progress"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[450px]">
                <LazyLoadedRateTableUsageMetrics
                  key={`rate-table-usage-metrics-${range}`}
                  title="Rate Table Usage Metrics"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[400px]">
                <LazyLoadedSubscriptionCount
                  key={`subscription-count-${range}`}
                  title="Subscription Count"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 lg:col-span-6 flex min-h-[400px]">
                <LazyLoadedExitPopupConversionFunnel
                  key={`exit-popup-conversion-funnel-${range}`}
                  title="Exit Popup Conversion Funnel"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[510px]">
                <LazyLoadedEmailStats
                  key={`email-stats-${range}`}
                  title="Email Stats"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
              <div className="col-span-12 flex min-h-[510px]">
                <LazyLoadedLeadList
                  key={`lead-list-${range}`}
                  title="Lead List"
                  range={range}
                  orgId={orgId}
                  loid={loid}
                />
              </div>
            </Fragment>
          )}
        </div>
      </Fragment>
    </RequireRole>
  )
}

export default Stats
